// Cart items structure: { albumId, title, artist, imageUrl, price, quantity }
const cartItems = new Map();

export const addToCart = (album) => {
  const itemId = album.position || album.title;

  // Don't allow duplicates - each item can only be added once
  if (cartItems.has(itemId)) {
    showCartNotification(`"${album.title}" is already in your cart!`);
    return;
  }

  cartItems.set(itemId, {
    id: itemId,
    title: album.title,
    photographer: album.photographer,
    description: album.description,
    imageUrl: album.image_url,
    price: album.price || 150, // Default price or from album data
    priceId: album.stripe_price || album.priceId || album.price_id, // Store the Stripe price ID
    quantity: 1, // Always 1
  });

  updateCartUI();
  showCartNotification(`Added "${album.title}" to cart!`);
};

export const removeFromCart = (itemId) => {
  cartItems.delete(itemId);
  updateCartUI();
};

export const updateQuantity = (itemId, newQuantity) => {
  if (newQuantity <= 0) {
    removeFromCart(itemId);
    return;
  }

  const item = cartItems.get(itemId);
  if (item) {
    item.quantity = newQuantity;
    updateCartUI();
  }
};

export const clearCart = () => {
  cartItems.clear();
  updateCartUI();
};

export const getCartItems = () => {
  return Array.from(cartItems.values());
};

export const getCartCount = () => {
  let count = 0;
  cartItems.forEach((item) => (count += item.quantity));
  return count;
};

export const calculateTotals = () => {
  let total = 0;

  cartItems.forEach((item) => {
    total += item.price * item.quantity;
  });

  return {
    total,
  };
};

const SERVER_URL = import.meta.env.VITE_SERVER_URL || "";

export const checkout = async () => {
  if (cartItems.size === 0) {
    alert("Your cart is empty!");
    return;
  }

  try {
    // Prepare line items for Stripe Checkout using price IDs
    const lineItems = Array.from(cartItems.values()).map((item) => ({
      price: item.priceId, // Use the price ID from the item
      quantity: item.quantity,
    }));

    // Send request to backend server
    const response = await fetch(`${SERVER_URL}/api/create-checkout-session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        lineItems,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create checkout session");
    }

    const { url } = await response.json();

    // Redirect to Stripe checkout page
    if (url) {
      window.location.href = url;
    } else {
      throw new Error("No checkout URL returned");
    }
  } catch (error) {
    console.error("Checkout error:", error);
    alert("An error occurred during checkout. Please try again.");
  }
};

export const showCartModal = () => {
  const cartModal = document.getElementById("cartModal");
  if (cartModal) {
    updateCartUI();
    cartModal.classList.add("show");
    document.body.style.overflow = "hidden";
  }
};

export const hideCartModal = () => {
  const cartModal = document.getElementById("cartModal");
  if (cartModal) {
    cartModal.classList.remove("show");
    document.body.style.overflow = "";
  }
};

const updateCartUI = () => {
  updateCartBadge();
  updateCartItemsList();
  updateCartSummary();
};

const updateCartBadge = () => {
  const checkoutBtn = document.getElementById("checkoutBtn");
  if (!checkoutBtn) return;

  let badge = checkoutBtn.querySelector(".cart-badge");
  const count = getCartCount();

  if (count > 0) {
    if (!badge) {
      badge = document.createElement("span");
      badge.className = "cart-badge";
      checkoutBtn.appendChild(badge);
    }
    badge.textContent = count;
  } else if (badge) {
    badge.remove();
  }
};

const updateCartItemsList = () => {
  const container = document.getElementById("cartItemsContainer");
  const emptyMessage = document.getElementById("emptyCartMessage");

  if (!container) return;

  // Clear existing items (except empty message)
  const existingItems = container.querySelectorAll(".cart-item");
  existingItems.forEach((item) => item.remove());

  if (cartItems.size === 0) {
    if (emptyMessage) emptyMessage.style.display = "block";
    return;
  }

  if (emptyMessage) emptyMessage.style.display = "none";

  cartItems.forEach((item, itemId) => {
    const cartItemEl = createCartItemElement(item, itemId);
    container.appendChild(cartItemEl);
  });
};

const createCartItemElement = (item, itemId) => {
  console.log(item);
  const div = document.createElement("div");
  div.className = "cart-item";
  div.innerHTML = `
    <img src="${item.imageUrl}" alt="${item.title}" class="cart-item-image" />
    <div class="cart-item-details">
      <div class="cart-item-meta">
        <span>${item.description}</span>
      </div>
      <div class="cart-item-price">$${item.price}</div>
    </div>
    <div class="cart-item-actions">
      <button class="remove-item-btn" data-id="${itemId}"><svg style="width: 16px; height:16px;" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trash-icon lucide-trash"><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>
    </div>
  `;

  // Add event listeners
  const removeBtn = div.querySelector(".remove-item-btn");

  removeBtn.addEventListener("click", () => {
    if (confirm(`Remove "${item.title}" from cart?`)) {
      removeFromCart(itemId);
    }
  });

  return div;
};

const updateCartSummary = () => {
  const totals = calculateTotals();

  const totalEl = document.getElementById("cartTotal");
  const checkoutBtn = document.getElementById("cartCheckoutBtn");

  if (totalEl) totalEl.textContent = `$${totals.total.toFixed(0)}`;

  if (checkoutBtn) {
    checkoutBtn.disabled = cartItems.size === 0;
  }
};

const showCartNotification = (message) => {
  // Simple notification (you can make this fancier)
  const notification = document.createElement("div");
  notification.style.cssText = `
    position: fixed;
    bottom: 30px;
    right: 30px;
    background: #10b981;
    color: white;
    padding: 15px 25px;
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
    z-index: 10000;
    animation: slideIn 0.3s ease;
  `;
  notification.textContent = message;
  document.body.appendChild(notification);

  setTimeout(() => {
    notification.style.animation = "slideOut 0.3s ease";
    setTimeout(() => notification.remove(), 300);
  }, 2000);
};

// Add CSS for notification animations
const style = document.createElement("style");
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(400px);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);
